import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  type HomeAggregatorEntry,
  type HomeCategoryEntry,
  type HomeOnAirEntry,
  type HomeRow,
  type HeroAction,
  type HeroSlide,
  type EventWhen,
  type HomeVodEntry,
  type SportEventView,
  type VodItem,
} from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import type { Translator } from '../i18n/i18n.js';
import { HERO_ROTATION_MS, nextSlide } from '@player/domain';
import { useSpatialFocus } from '../useSpatialFocus.js';
import { PosterTile } from '../components/PosterTile.js';
import { channelColorOf } from '../channelColors.js';

/**
 * La Home (03-SCHERMATE-TV §3.1): le fasce che l'operatore ha configurato, nel suo ordine.
 *
 * ## Cosa decide questa schermata, e cosa no
 *
 * Disegna. **Quali fasce e in che ordine** l'ha già deciso `homeRows` con la vetrina del backend; qui
 * si sceglie solo come si vede una fascia di locandine rispetto a una di canali. Se le due cose
 * stessero insieme, cambiare l'aspetto di una piastrella vorrebbe dire rimettere le mani nella
 * logica che decide cosa mostrare.
 *
 * ## Una home senza fasce si dichiara
 *
 * Può succedere: catalogo non ancora scaricato, EPG non ancora letto, nessun titolo iniziato. Uno
 * schermo bianco sotto il menu è indistinguibile da un guasto — quindi si scrive cosa sta
 * succedendo, invece di lasciare il vuoto a raccontarlo.
 *
 * ## Il fuoco è quello del DOM
 *
 * Come nella ricerca: le fasce hanno forme diverse e il numero di elementi cambia quando arrivano i
 * dati. `useSpatialFocus` va dove sono le cose sullo schermo, invece di tenere un indice che
 * andrebbe ricalcolato a ogni caricamento.
 */

export interface HomeScreenProps {
  readonly focusPort: FocusPort;
  /** Le slide dell'hero, già risolte: quelle che non portano da nessuna parte non arrivano qui. */
  readonly heroSlides: ReadonlyArray<HeroSlide>;
  /** Vero quando le animazioni sono accese: l'hero ruota solo allora (07-PRESTAZIONI §5). */
  readonly animations: boolean;
  readonly onHeroAction: (action: HeroAction) => void;
  readonly rows: ReadonlyArray<HomeRow>;
  /** Vero finché il catalogo e la guida non sono arrivati: cambia cosa dice la home vuota. */
  readonly loading: boolean;
  readonly onOpenVod: (item: VodItem) => void;
  readonly onOpenChannel: (channelId: string) => void;
  readonly onOpenCategory: (categoryId: string) => void;
  readonly onOpenAggregator: (aggregatorId: string) => void;
  readonly onExit: () => void;
}

export function HomeScreen({
  focusPort,
  heroSlides: slides,
  animations,
  onHeroAction,
  rows,
  loading,
  onOpenVod,
  onOpenChannel,
  onOpenCategory,
  onOpenAggregator,
  onExit,
}: HomeScreenProps): ReactNode {
  const translator = useTranslator();
  const frameRef = useRef<HTMLDivElement | null>(null);

  const handlers = useRef({ onOpenVod, onOpenChannel, onOpenCategory, onOpenAggregator, onExit, onHeroAction });
  useEffect(() => {
    handlers.current = { onOpenVod, onOpenChannel, onOpenCategory, onOpenAggregator, onExit, onHeroAction };
  }, [onOpenVod, onOpenChannel, onOpenCategory, onOpenAggregator, onExit, onHeroAction]);

  useSpatialFocus(focusPort, frameRef, {
    onBack: () => {
      handlers.current.onExit();
      return true;
    },
  });

  return (
    <div
      ref={frameRef}
      data-home="1"
      style={{
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        padding: '56px 0 80px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 34,
      }}
    >
      {slides.length > 0 && (
        <Hero slides={slides} animations={animations} onAction={(action) => handlers.current.onHeroAction(action)} />
      )}

      <h1 style={{ font: `800 64px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0, padding: '0 72px' }}>
        {translator.t('home.title')}
      </h1>

      {rows.length === 0 ? (
        <div data-home-empty="1" style={{ padding: '40px 72px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={{ font: `700 34px ${FONT_STACK}` }}>
            {translator.t(loading ? 'home.loading' : 'home.empty')}
          </span>
          <span style={{ font: `500 24px ${FONT_STACK}`, color: colors.textDim }}>
            {translator.t(loading ? 'home.loadingHint' : 'home.emptyHint')}
          </span>
        </div>
      ) : (
        rows.map((row, index) => (
          <Row key={`${row.kind}-${index}`} row={row} handlers={handlers} />
        ))
      )}
    </div>
  );
}

/**
 * Il riquadro in cima: una slide alla volta, che ruota.
 *
 * ## La rotazione si ferma quando il fuoco è dentro
 *
 * È la regola che distingue una rotazione elegante da una tortura: se cambia mentre ci si sta
 * muovendo sopra, il pulsante si sposta sotto il dito e si preme quello sbagliato. Si ferma anche
 * quando le animazioni sono spente, perché l'impostazione deve avere un effetto misurabile — una voce
 * che non fa niente insegna che le impostazioni non servono.
 *
 * ## I pallini sono tasti
 *
 * Non indicatori: ci si può andare sopra e scegliere la slide. Con cinque elementi e sette secondi,
 * aspettare il giro per rivedere il primo è un minuto — e nessuno aspetta.
 */
/**
 * «Stasera alle 20:45 · Sul canale Arena Calcio».
 *
 * ## Cinque frasi intere, non una fatta di pezzi
 *
 * Ogni caso ha la sua chiave di traduzione completa. Comporre «Stasera» + «alle» + l'ora da tre
 * frammenti funziona in italiano e si rompe appena una lingua mette le parole in un altro ordine —
 * ed è il modo in cui si finisce con sette traduzioni che nessuno può scrivere bene. In turco
 * l'orario non vuole nessuna preposizione; in tedesco «Heute Abend um» è una cosa sola.
 *
 * Il caso `date` è l'unico che riceve due parametri: la data col nome del giorno e l'ora.
 *
 * ## `when` può essere nullo, e allora resta il canale
 *
 * Succede se l'istante del server non si conosce. Si scrive dove si guarda e **non quando**, invece
 * di indovinare un giorno: «Stasera» detto a mezzogiorno è peggio di niente.
 */
/*
  Esportata perché la usa **anche la home del telefono**.

  È una frase composta da cinque pezzi tradotti — «Stasera alle 20:45 · Sul canale Arena Calcio» — e
  riscriverla là vorrebbe dire due composizioni da tenere d'accordo in sette lingue: il giorno che
  una cambia, due apparecchi annunciano lo stesso evento con parole diverse.
*/
export function eventLine(translator: Translator, when: EventWhen | null, channel: string | null): string {
  const parts: string[] = [];

  if (when !== null) {
    const time = translator.time(when.startMs);
    parts.push(
      when.kind === 'date'
        ? translator.t('hero.when.date', { date: translator.weekdayDate(when.startMs), time })
        : translator.t(`hero.when.${when.kind}`, { time }),
    );
  }

  if (channel !== null && channel !== '') {
    parts.push(translator.t('hero.when.onChannel', { channel }));
  }
  return parts.join(' · ');
}

function Hero({
  slides,
  animations,
  onAction,
}: {
  slides: ReadonlyArray<HeroSlide>;
  animations: boolean;
  onAction: (action: HeroAction) => void;
}): ReactNode {
  const translator = useTranslator();
  const [at, setAt] = useState(0);
  const [focusInside, setFocusInside] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  /*
    L'indice torna in campo se le slide diminuiscono.

    Succede davvero: un evento finisce e la sua slide sparisce mentre la home è aperta. Senza questa
    riga l'hero resterebbe su un indice che non esiste più, cioè vuoto.
  */
  const index = at < slides.length ? at : 0;
  const slide = slides[index];

  useEffect(() => {
    if (!animations || focusInside || slides.length < 2) return undefined;
    const timer = setInterval(() => setAt((current) => nextSlide(current, slides.length)), HERO_ROTATION_MS);
    return () => clearInterval(timer);
  }, [animations, focusInside, slides.length]);

  if (slide === undefined) return null;

  return (
    <div
      ref={boxRef}
      data-hero="1"
      data-hero-slide={slide.id}
      onFocus={() => setFocusInside(true)}
      onBlur={(event) => {
        // Il fuoco che passa da un tasto all'altro **dentro** l'hero non è un'uscita: senza questo
        // controllo la rotazione ripartirebbe a ogni spostamento laterale.
        if (!boxRef.current?.contains(event.relatedTarget as Node | null)) setFocusInside(false);
      }}
      style={{
        position: 'relative',
        minHeight: 680,
        margin: '-56px 0 0',
        padding: '120px 72px 44px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        gap: 16,
        overflow: 'hidden',
      }}
    >
      {slide.image !== null && (
        <img
          src={slide.image}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
      )}
      {/* Il velo: senza, un titolo bianco su una locandina chiara non si legge. Con gli effetti
          spenti diventa un fondo pieno, non sparisce. */}
      <span style={{ position: 'absolute', inset: 0, background: 'var(--ov, rgba(10,13,19,.82))', zIndex: 0 }} />

      <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 16 }}>
        {slide.live && (
          <span
            data-hero-live="1"
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              background: colors.live,
              color: colors.text,
              font: `800 20px ${FONT_STACK}`,
              letterSpacing: '.04em',
            }}
          >
            {`● ${translator.t('search.live')}`}
          </span>
        )}
        {slide.kicker !== '' && (
          <span
            style={{
              font: `800 22px ${FONT_STACK}`,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: colors.accent,
            }}
          >
            {slide.kicker.startsWith('hero.') ? translator.t(slide.kicker) : slide.kicker}
          </span>
        )}
      </span>

      <span style={{ position: 'relative', zIndex: 1, font: `800 72px/1.05 ${FONT_STACK}`, letterSpacing: '-.02em' }}>
        {slide.title}
      </span>

      {/*
        La riga sotto il titolo: per un film `Azione · 2024`, per un evento **quando è e dove**.

        Per un evento non arriva composta dal dominio: arrivano un caso (`when`) e il nome del
        canale, e la frase si costruisce qui, dove ci sono la tabella delle sette lingue e `Intl` —
        perché «Stasera alle 20:45» non è, parola per parola, la stessa costruzione in polacco, e il
        nome del giorno lo sa `Intl.DateTimeFormat` e non noi.
      */}
      {/*
        La condizione guarda **tutti e tre** i campi.

        Con il solo `meta` e `channel` un evento di cui si sapesse l'orario ma non il canale faceva
        sparire la riga intera: l'orario c'era e non si vedeva. Oggi non può succedere — l'hero non
        disegna un evento senza un canale che questa linea possieda — ma qui non si dipende da un
        invariante che vive in un altro file: il tipo dice `string | null`, e si disegna quel che c'è.
      */}
      {(slide.meta !== '' || slide.when !== null || slide.channel !== null) && (
        <span
          data-hero-meta="1"
          style={{ position: 'relative', zIndex: 1, font: `500 26px ${FONT_STACK}`, color: colors.textMuted }}
        >
          {slide.when === null && slide.channel === null
            ? slide.meta
            : eventLine(translator, slide.when, slide.channel)}
        </span>
      )}

      <span style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 14, paddingTop: 8 }}>
        <button
          type="button"
          data-f="1"
          data-hero-primary="1"
          onClick={() => onAction(slide.primary.action)}
          style={heroPrimaryButton}
        >
          {translator.t(slide.primary.labelKey)}
        </button>
        {slide.secondary !== null && (
          <button
            type="button"
            data-f="1"
            data-hero-secondary="1"
            onClick={() => onAction(slide.secondary!.action)}
            style={heroSecondaryButton}
          >
            {translator.t(slide.secondary.labelKey)}
          </button>
        )}
      </span>

      {slides.length > 1 && (
        <span style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 10, paddingTop: 10 }}>
          {slides.map((entry, dot) => (
            <button
              key={entry.id}
              type="button"
              data-f="1"
              data-hero-dot={dot}
              data-selected={dot === index ? '1' : '0'}
              onClick={() => setAt(dot)}
              style={{
                width: dot === index ? 44 : 22,
                height: 12,
                borderRadius: 6,
                border: 0,
                padding: 0,
                background: dot === index ? colors.accent : colors.surfaceButton,
                cursor: 'pointer',
                transition: 'var(--tr, width .16s ease)',
              }}
            />
          ))}
        </span>
      )}
    </div>
  );
}

const heroPrimaryButton = {
  minHeight: 68,
  padding: '0 34px',
  borderRadius: 12,
  border: 0,
  background: colors.text,
  color: colors.background,
  font: `700 26px ${FONT_STACK}`,
  cursor: 'pointer',
} as const;

const heroSecondaryButton = {
  ...heroPrimaryButton,
  background: colors.surfaceButton,
  color: colors.text,
} as const;

type Handlers = {
  onOpenVod: (item: VodItem) => void;
  onOpenChannel: (channelId: string) => void;
  onOpenCategory: (categoryId: string) => void;
  onOpenAggregator: (aggregatorId: string) => void;
  onExit: () => void;
  onHeroAction: (action: HeroAction) => void;
};

function Row({ row, handlers }: { row: HomeRow; handlers: { current: Handlers } }): ReactNode {
  const translator = useTranslator();
  /*
    Il titolo dell'operatore vince su quello predefinito.

    `title` arriva dalla vetrina già risolto nella lingua di chi guarda; quando è vuoto si usa il
    nome della fascia dall'i18n. Il contrario — il nostro sempre — renderebbe inutile il campo che
    l'operatore riempie nel pannello.
  */
  const title = row.title ?? translator.t(`home.row.${row.kind}`);

  return (
    <section data-home-row={row.kind} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: '0 72px', display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ font: `700 34px ${FONT_STACK}` }}>{title}</span>
        {row.kind === 'recs' && (
          <span style={{ font: `500 22px ${FONT_STACK}`, color: colors.textDim }}>
            {translator.t('home.row.recs.note')}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 16, overflowX: 'auto', scrollbarWidth: 'none', padding: '8px 72px 12px' }}>
        {cardsOf(row, handlers)}
      </div>
    </section>
  );
}

/**
 * Le piastrelle di una fascia.
 *
 * Uno `switch` e non una catena di ternari: con i ternari TypeScript non restringe il tipo dell'unione
 * — `row.events` risultava inesistente su un ramo dove esiste — e l'unico modo di farlo compilare
 * sarebbe stato un `as`, cioè spegnere il controllo che serve proprio qui, dove le fasce hanno forme
 * diverse e se ne aggiungeranno altre.
 */
function cardsOf(row: HomeRow, handlers: { current: Handlers }): ReactNode {
  switch (row.kind) {
    case 'continue':
    case 'recs':
    case 'latest':
    case 'picks':
      return row.items.map((entry) => (
        <VodCard key={entry.item.id} entry={entry} onOpen={() => handlers.current.onOpenVod(entry.item)} />
      ));
    case 'onair':
      return row.channels.map((entry) => (
        <OnAirCard
          key={entry.channel.id}
          entry={entry}
          onOpen={() => handlers.current.onOpenChannel(entry.channel.id)}
        />
      ));
    case 'cats':
      return row.categories.map((entry) => (
        <CategoryCard key={entry.id} entry={entry} onOpen={() => handlers.current.onOpenCategory(entry.id)} />
      ));
    case 'aggs':
      return row.aggregators.map((entry) => (
        <AggregatorCard key={entry.id} entry={entry} onOpen={() => handlers.current.onOpenAggregator(entry.id)} />
      ));
    case 'events':
      return row.events.map((entry) => (
        <EventCard
          key={entry.view.event.id}
          view={entry.view}
          /*
            Il canale **che questa linea possiede**, scelto dal dominio: al player serve cosa
            riprodurre, e un nome di emittente non si riproduce.

            Era `channelIds[0]`, cioè il primo che il server aveva messo in fila — e quell'elenco è
            comune a tutta l'installazione, non a questa linea. Su chi non vende quel canale la card
            si disegnava, diceva «in diretta», e premendo OK non faceva niente. Vedi
            `playableChannelOf`.
          */
          channel={entry.channel}
          onOpen={() => {
            if (entry.channel !== null) handlers.current.onOpenChannel(entry.channel.id);
          }}
        />
      ));
  }
}

function VodCard({ entry, onOpen }: { entry: HomeVodEntry; onOpen: () => void }): ReactNode {
  const translator = useTranslator();
  const parts: string[] = [
    translator.t(entry.item.kind === 'series' ? 'vod.kind.series' : 'vod.kind.movie'),
  ];
  if (entry.item.genre.trim() !== '') parts.push(entry.item.genre);
  if (entry.item.year > 0) parts.push(String(entry.item.year));

  return (
    <PosterTile
      title={entry.item.title}
      subtitle={parts.join(' · ')}
      posterUrl={entry.item.posterUrl}
      percent={entry.state.percent > 0 ? entry.state.percent : null}
      watched={entry.state.watched}
      lockedRating={null}
      // Il motivo del consiglio è un **titolo**, e la frase la compone l'i18n: «Perché hai visto X»
      // non si dice uguale in sette lingue, e concatenarla qui la porterebbe fuori dall'i18n.
      note={entry.because === null ? null : translator.t('home.because', { title: entry.because })}
      focused={false}
      onSelect={onOpen}
    />
  );
}

function OnAirCard({ entry, onOpen }: { entry: HomeOnAirEntry; onOpen: () => void }): ReactNode {
  const translator = useTranslator();
  const color = channelColorOf(entry.channel.name);
  const time = (ms: number): string =>
    new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <button
      type="button"
      data-f="1"
      data-home-channel={entry.channel.id}
      onClick={onOpen}
      style={{
        flex: 'none',
        width: 420,
        minHeight: 200,
        padding: 20,
        borderRadius: 16,
        border: 0,
        background: colors.surface,
        color: colors.text,
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        cursor: 'pointer',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            flex: 'none',
            width: 56,
            height: 56,
            borderRadius: 10,
            overflow: 'hidden',
            background: color.background,
            color: color.foreground,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: `800 20px ${FONT_STACK}`,
          }}
        >
          {entry.channel.logoUrl === null ? (
            entry.channel.number
          ) : (
            <img src={entry.channel.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          )}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            font: `700 24px ${FONT_STACK}`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {entry.channel.name}
        </span>
      </span>

      <span style={{ font: `700 26px ${FONT_STACK}`, wordBreak: 'break-word' }}>
        {entry.now?.title ?? ''}
      </span>

      {entry.percent !== null && (
        <span style={{ display: 'block', height: 6, borderRadius: 3, background: colors.surfaceRaised }}>
          <span
            data-progress={entry.percent}
            style={{
              display: 'block',
              height: '100%',
              width: `${entry.percent}%`,
              borderRadius: 3,
              background: colors.accent,
            }}
          />
        </span>
      )}

      {entry.next !== null && (
        <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('home.next', { at: time(entry.next.startMs), title: entry.next.title })}
        </span>
      )}
    </button>
  );
}

function CategoryCard({ entry, onOpen }: { entry: HomeCategoryEntry; onOpen: () => void }): ReactNode {
  const translator = useTranslator();
  return (
    <button
      type="button"
      data-f="1"
      data-home-category={entry.id}
      onClick={onOpen}
      style={{
        flex: 'none',
        width: 300,
        minHeight: 130,
        padding: 20,
        borderRadius: 16,
        border: 0,
        background: colors.surface,
        color: colors.text,
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 8,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          font: `700 25px/1.2 ${FONT_STACK}`,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          wordBreak: 'break-word',
        }}
      >
        {entry.name}
      </span>
      <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
        {translator.t('guide.channelCount', { count: entry.channelCount })}
      </span>
    </button>
  );
}

/**
 * Una piastrella di «Esplora».
 *
 * Con uno sfondo caricato dall'operatore l'immagine sta **sopra il colore, non al suo posto**: il
 * colore riempie il riquadro e regge quando l'immagine non arriva — rete lenta, file tolto dal
 * disco. Senza, la piastrella sarebbe nera con sopra del testo bianco proprio mentre il cliente
 * guarda la vetrina per la prima volta.
 *
 * E sopra l'immagine c'è una velatura scura. Non è decorazione: una fotografia chiara sotto
 * un'etichetta bianca la rende illeggibile, e non è qualcosa che l'operatore possa prevedere mentre
 * carica il file — lo scopre un suo cliente, e non lo racconta a nessuno.
 */
function AggregatorCard({ entry, onOpen }: { entry: HomeAggregatorEntry; onOpen: () => void }): ReactNode {
  const hasImage = entry.imageUrl !== null;

  return (
    <button
      type="button"
      data-f="1"
      data-home-aggregator={entry.id}
      data-aggregator-image={hasImage ? '1' : undefined}
      onClick={onOpen}
      style={{
        position: 'relative',
        flex: 'none',
        width: 280,
        minHeight: 130,
        padding: '20px 20px 20px 16px',
        borderRadius: 16,
        border: 0,
        overflow: 'hidden',
        // La barra di colore del prototipo: è il colore che l'operatore ha scelto nel pannello.
        // Resta anche con lo sfondo, perché è quel che identifica la piastrella da lontano.
        borderLeft: `8px solid ${entry.color}`,
        background: hasImage ? entry.color : colors.surface,
        color: colors.text,
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      {hasImage && (
        <>
          <img
            src={entry.imageUrl ?? undefined}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            loading="lazy"
            decoding="async"
          />
          <span
            data-aggregator-scrim="1"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(0,0,0,.78) 0%, rgba(0,0,0,.35) 100%)',
            }}
          />
        </>
      )}
      <span
        style={{
          position: 'relative',
          zIndex: 1,
          font: `700 27px/1.2 ${FONT_STACK}`,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          wordBreak: 'break-word',
        }}
      >
        {entry.label}
      </span>
    </button>
  );
}

function EventCard({
  view,
  channel,
  onOpen,
}: {
  view: SportEventView;
  /** Il canale di questa linea su cui si può guardare, o `null`: lo decide il dominio. */
  channel: { readonly id: string; readonly name: string } | null;
  onOpen: () => void;
}): ReactNode {
  const translator = useTranslator();
  const live = view.state === 'live';
  /*
    «Si può guardare» significa **questa linea**, non «esiste un canale al mondo».

    Era `channelIds.length > 0`, cioè vero anche quando tutti quei canali erano di un altro
    pacchetto: la card restava premibile e non apriva niente.
  */
  const playable = channel !== null;
  const time = new Date(view.event.startMs).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <button
      type="button"
      data-f="1"
      data-home-event={view.event.id}
      data-live={live ? '1' : '0'}
      onClick={onOpen}
      disabled={!playable}
      style={{
        flex: 'none',
        width: 360,
        minHeight: 170,
        padding: 20,
        borderRadius: 16,
        border: 0,
        background: colors.surface,
        color: colors.text,
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        // Un evento senza canale non si apre: si mostra spento invece di sembrare premibile.
        opacity: playable ? 1 : 0.55,
        cursor: playable ? 'pointer' : 'default',
      }}
    >
      <span
        style={{
          alignSelf: 'flex-start',
          padding: '6px 14px',
          borderRadius: 8,
          font: `800 18px ${FONT_STACK}`,
          letterSpacing: '.04em',
          background: live ? colors.live : colors.info,
          color: live ? colors.background : colors.text,
        }}
      >
        {live ? translator.t('search.live') : time}
      </span>
      <span style={{ font: `700 26px/1.2 ${FONT_STACK}`, wordBreak: 'break-word' }}>
        {view.event.title}
      </span>
      {view.event.competition !== '' && (
        <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
          {view.event.competition}
        </span>
      )}
    </button>
  );
}
